import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Tab,
    Table,
    Form,
    Modal,
    Alert,
    Row,
    Col,
} from 'react-bootstrap';
import DashboardNav, {
    generateChildId,
} from '@/components/common/DashboardNav';
import ProgramDeadlinesSection from '@/components/common/ProgramDeadlinesSection';
import BudgetStatus, { budgetOrNone } from '@/features/programs/BudgetStatus';
import CoordinatorSettings from '@/features/programs/CoordinatorSettings';
import ProgramTermHeader, {
    MissingDatesAlert,
} from '@/components/common/ProgramTermHeader';
import ProgramPageLinkCard from '@/features/programs/ProgramPageLinkCard';
import ProgramCoursePrefixes from '@/components/common/ProgramCoursePrefixes';
import DecisionEmailTemplates from '@/features/programs/DecisionEmailTemplates';
import programService from '@/shared/services/programService';
import TrainingDatesCard from '@/features/programs/TrainingDatesCard';
import ProgramNotificationSettings from '@/features/programs/ProgramNotificationSettings';
import taRequestService from '@/shared/services/taRequestService';
import CoordinatorOverview from '@/features/programs/CoordinatorOverview';
import ListFilterNote from '@/features/programs/ListFilterNote';
import UnsubmittedDrafts from '@/features/programs/UnsubmittedDrafts';
import {
    TA_REQUEST_LISTS,
    DRAFTS_SHOW,
    listFor,
} from '@/features/programs/overviewLists';
import { useDashboardTab } from '@/shared/hooks/useDashboardTab';
import { useListLinkReset } from '@/shared/hooks/useListLinkReset';
import assignmentService from '@/shared/services/assignmentService';
import termService from '@/shared/services/termService';
import budgetService from '@/shared/services/budgetService';
import '@/features/programs/CoordinatorDashboard.css';
import RequestCardList from '@/features/programs/RequestCardList';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { badgeTextFor } from '@/shared/utils/badgeText';
import ActionBar from '@/components/common/ActionBar';
import SortableTh from '@/components/common/SortableTh';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import { PHONE_QUERY, SECONDARY_COLUMN } from '@/shared/utils/tableControls';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { useTableParams } from '@/shared/hooks/useTableParams';
import {
    ASSIGNMENT_STATUS_ORDER,
    assignmentStatusLabel,
    courseCode,
    courseColumn,
    courseOptionsFor,
    dateColumn,
    departmentOf,
    departmentOptionsFor,
    formatCalendarDate,
    personColumn,
    pillOptions,
    statusColumn,
} from '@/features/programs/coordinatorColumns';
import StatusPill from '@/components/common/StatusPill';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import settledRead from '@/shared/utils/settledRead';
import { TA_REQUEST_PILL, pillFor } from '@/shared/utils/statusPills';
import { formatAnswerDay } from '@/shared/utils/deadline';
import { termDisplayName } from '@/shared/utils/termLabel';

// The server marks a pending request whose 900 number and email belong to
// two accounts; approving it is refused until faculty correct one
const ACCOUNT_MISMATCH_BADGE = '900 number and email do not match';

const REQUEST_STATUS_ORDER = [
    'pending',
    'processing',
    'approved',
    'converted',
    'denied',
    'cancelled',
];

const REQUEST_STATUS_OPTIONS = pillOptions(TA_REQUEST_PILL, [
    'pending',
    'approved',
    'converted',
    'denied',
    'cancelled',
]);

// The named student's 900 number is shown in the table but never exported
const REQUEST_COLUMNS = [
    {
        key: 'faculty',
        label: 'Faculty',
        type: 'text',
        value: (req) => req.requestingFaculty?.name || 'Unknown',
        csv: (req) =>
            req.requestingFaculty?.department
                ? `${req.requestingFaculty?.name || 'Unknown'} (${req.requestingFaculty.department})`
                : req.requestingFaculty?.name || 'Unknown',
    },
    courseColumn((req) => req.course),
    {
        key: 'student',
        label: 'Named Student',
        type: 'text',
        secondary: true,
        value: (req) => req.namedStudent?.name,
        csv: (req) => req.namedStudent?.name || 'Not specified',
    },
    {
        key: 'hours',
        label: 'Hours',
        type: 'number',
        secondary: true,
        searchable: false,
        value: (req) => req.requestedHours || null,
        csv: (req) =>
            req.requestedHours ? `${req.requestedHours} hrs/wk` : '',
    },
    {
        ...dateColumn('submitted', 'Submitted', (req) => req.submittedAt),
        secondary: true,
    },
    statusColumn(
        REQUEST_STATUS_ORDER,
        (req) => req.status,
        (req) => pillFor('taRequest', req.status).label
    ),
];

const ASSIGNMENT_COLUMNS = [
    personColumn('ta', 'TA', (ta) => ta.peerSupporter),
    {
        key: 'supervisor',
        label: 'Supervisor',
        type: 'text',
        secondary: true,
        value: (ta) => ta.supervisor?.name || ta.instructorName,
    },
    courseColumn((ta) => ta.course),
    {
        key: 'hours',
        label: 'Hours/Week',
        type: 'number',
        secondary: true,
        searchable: false,
        value: (ta) => ta.hoursPerWeek || null,
    },
    {
        ...dateColumn(
            'start',
            'Start Date',
            (ta) => ta.startDate,
            formatCalendarDate
        ),
        secondary: true,
    },
    statusColumn(
        ASSIGNMENT_STATUS_ORDER,
        (ta) => ta.status,
        (ta) => assignmentStatusLabel(ta.status)
    ),
];

const requestStatusOf = (req) => req.status;
const requestDepartment = (req) => departmentOf(req.course);
const assignmentCourseOf = (ta) => courseCode(ta.course);

// Tab keys that moved into the Settings tab, with the section each landed in
const SETTINGS_ALIASES = {
    notifications: { tab: 'settings', show: 'notifications' },
    'decision-emails': { tab: 'settings', show: 'decision-emails' },
};

function TACoordinatorDashboard() {
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [stats, setStats] = useState(null);
    const [requests, setRequests] = useState([]);
    // The server's real total when the whole-term list hit its backstop
    const [cutOffTotal, setCutOffTotal] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [budgetFailed, setBudgetFailed] = useState(false);
    const { activeTab, show, selectTab, clearShow } = useDashboardTab(
        ['overview', 'requests', 'active', 'settings'],
        'overview',
        SETTINGS_ALIASES
    );
    const [deadlinesOpen, setDeadlinesOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [decisionNotes, setDecisionNotes] = useState('');
    // 'approve' or 'reject' while the dialog asks to confirm that decision
    const [confirming, setConfirming] = useState(null);
    // The decision button that asked, so Go back returns focus to it: the
    // footer's buttons are replaced between the two steps
    const askedRef = useRef(null);
    const focusConfirm = useCallback((el) => el?.focus(), []);
    const focusDeclineIfAsked = useCallback((el) => {
        if (el && askedRef.current === 'reject') el.focus();
    }, []);
    const focusApproveIfAsked = useCallback((el) => {
        if (el && askedRef.current === 'approve') el.focus();
    }, []);
    const [submittingDecision, setSubmittingDecision] = useState(false);
    // Shown in the review dialog, which covers the page's own alert
    const [decisionError, setDecisionError] = useState(null);
    const [budget, setBudget] = useState(null);

    // Load terms on mount
    useEffect(() => {
        const loadTerms = async () => {
            try {
                const data = await termService.getAllTerms();
                setTerms(data);
                const active = data.find((t) => t.isActive) || data[0];
                if (active) {
                    setSelectedTerm(active);
                } else {
                    setLoading(false);
                }
            } catch (_err) {
                setError('Failed to load terms.');
                setLoading(false);
            }
        };
        loadTerms();
    }, []);

    const loadTermData = useCallback(async (termCode) => {
        try {
            setLoading(true);
            setError(null);
            setLoadFailed(false);

            const [statsData, requestsData, assignmentsData, budgetData] =
                await Promise.all([
                    programService.getTAStats(termCode),
                    taRequestService.getAllRequests({ term: termCode }),
                    // The tab and the header count the same people: the
                    // program stats count active assignments
                    assignmentService.getAssignmentsByProgram('ta', {
                        term: termCode,
                        status: 'active',
                    }),
                    settledRead(
                        budgetOrNone(budgetService.getBudget('ta', termCode))
                    ),
                ]);

            setStats(statsData);
            setRequests(requestsData);
            setCutOffTotal(requestsData.truncated ? requestsData.total : null);
            setAssignments(assignmentsData);
            setBudget(budgetData.value);
            setBudgetFailed(budgetData.failed);
        } catch (_err) {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedTerm?.code) {
            loadTermData(selectedTerm.code);
        }
    }, [selectedTerm?.code, loadTermData]);

    const handleTermChange = (code) => {
        const term = terms.find((t) => t.code === code);
        if (term) setSelectedTerm(term);
    };

    const handleTermUpdated = (updatedTerm) => {
        setSelectedTerm(updatedTerm);
        setTerms((prev) =>
            prev.map((t) => (t.code === updatedTerm.code ? updatedTerm : t))
        );
    };

    const requestList =
        activeTab === 'requests' ? listFor(TA_REQUEST_LISTS, show) : null;
    const listedRequests = requestList
        ? requests.filter(requestList.matches)
        : requests;
    const requestDepartmentOptions = departmentOptionsFor(
        requests,
        (req) => req.course
    );
    const requestControls = useTableControls({
        rows: listedRequests,
        columns: REQUEST_COLUMNS,
        statusOf: requestStatusOf,
        departmentOf: requestDepartment,
        departmentOptions: requestDepartmentOptions,
        persist: useTableParams('requests'),
    });
    const asCards = useMediaQuery(PHONE_QUERY);
    const requestCardOf = (req) => ({
        key: req._id,
        heading: courseCode(req.course) || 'Course',
        title: req.course?.title,
        faculty: req.requestingFaculty?.name || 'Unknown',
        pill: <StatusPill family="taRequest" value={req.status} />,
        facts: [
            req.namedStudent?.name && `Named student: ${req.namedStudent.name}`,
            req.requestedHours && `${req.requestedHours} hrs/wk`,
            req.submittedAt && `Submitted ${formatAnswerDay(req.submittedAt)}`,
        ].filter(Boolean),
        note: req.accountMismatch && (
            <Badge
                bg="warning"
                text={badgeTextFor('warning')}
                className="text-wrap text-start"
            >
                {ACCOUNT_MISMATCH_BADGE}
            </Badge>
        ),
        actionLabel: 'Review request',
        onAction: () => handleOpenReview(req),
    });
    const assignmentControls = useTableControls({
        rows: assignments,
        columns: ASSIGNMENT_COLUMNS,
        statusOf: assignmentCourseOf,
        persist: useTableParams('tas'),
    });
    const {
        setSearch: setRequestSearch,
        setStatus: setRequestStatus,
        setDepartment: setRequestDepartment,
    } = requestControls;

    // A list an Overview link opened starts from exactly the rows it counted
    useListLinkReset(
        show,
        useCallback(() => {
            setRequestSearch('');
            setRequestStatus('all');
            setRequestDepartment('all');
        }, [setRequestSearch, setRequestStatus, setRequestDepartment])
    );

    const assignmentCourseOptions = courseOptionsFor(
        assignments,
        (ta) => ta.course
    );

    const handleOpenReview = (request) => {
        setSelectedRequest(request);
        setDecisionNotes('');
        setDecisionError(null);
        setConfirming(null);
        askedRef.current = null;
        setShowReviewModal(true);
    };

    const closeReview = () => {
        setConfirming(null);
        setShowReviewModal(false);
    };

    const handleDecision = async (decision) => {
        if (!selectedRequest) return;

        try {
            setSubmittingDecision(true);
            setDecisionError(null);

            if (decision === 'approve') {
                await taRequestService.approveRequest(selectedRequest._id, {
                    notes: decisionNotes || undefined,
                });
            } else if (decision === 'reject') {
                await taRequestService.denyRequest(
                    selectedRequest._id,
                    decisionNotes || 'Request declined by TA coordinator.'
                );
            }

            closeReview();
            setSelectedRequest(null);
            setDecisionNotes('');

            // Refresh requests list for the current term
            if (selectedTerm?.code) {
                await loadTermData(selectedTerm.code);
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to submit decision. Please try again.';
            setDecisionError(message);
        } finally {
            setSubmittingDecision(false);
        }
    };

    // Where the requests stand is the Overview's Requests line, from one
    // source; the header keeps the figures nothing else on the tab shows
    const displayStats = [
        {
            value:
                stats?.activeTAs ??
                assignments.filter((a) => a.status === 'active').length,
            label: 'Active TAs',
            color: 'success',
        },
        {
            value:
                stats?.pendingHRProcessing ??
                requests.filter((r) => r.status === 'approved').length,
            label: 'Pending HR',
            color: 'info',
        },
    ];

    const manageDates = () => {
        selectTab('settings', 'deadlines');
        setDeadlinesOpen(true);
    };

    const navGroups = [
        {
            key: 'overview',
            label: 'Overview',
            tabs: [{ key: 'overview', label: 'Overview' }],
        },
        {
            key: 'requests',
            label: `Faculty Requests (${cutOffTotal ?? requests.length})`,
            tabs: [
                {
                    key: 'requests',
                    label: `Faculty Requests (${cutOffTotal ?? requests.length})`,
                },
            ],
        },
        {
            key: 'active',
            label: `Active TAs (${assignments.length})`,
            tabs: [
                { key: 'active', label: `Active TAs (${assignments.length})` },
            ],
        },
        {
            key: 'settings',
            label: 'Settings',
            tabs: [{ key: 'settings', label: 'Settings' }],
        },
    ];

    const settingsSections = [
        {
            key: 'courses-served',
            title: 'Courses served',
            content: (
                <>
                    <ProgramCoursePrefixes programType="ta" programLabel="TA" />
                    <Alert
                        variant="light"
                        className="mb-0 border coordinator-info-alert-ta"
                    >
                        <strong>TA Request Process:</strong> Faculty submit
                        requests with a named student. The coordinator reviews
                        and approves requests before coordinating with HR for
                        employment processing.
                    </Alert>
                </>
            ),
        },
        {
            key: 'deadlines',
            title: 'Application deadlines',
            content: (
                <ProgramDeadlinesSection
                    programType="ta"
                    programLabel="TA"
                    programName="Teaching Assistants"
                    selectedTerm={selectedTerm}
                    onTermUpdated={handleTermUpdated}
                    show={deadlinesOpen}
                    onShow={() => setDeadlinesOpen(true)}
                    onHide={() => setDeadlinesOpen(false)}
                />
            ),
        },
        {
            key: 'notifications',
            title: 'Notifications',
            content: <ProgramNotificationSettings programType="ta" />,
        },
        {
            key: 'training-dates',
            title: 'Training dates',
            content: (
                <TrainingDatesCard
                    programType="ta"
                    termCode={selectedTerm?.code}
                    termName={termDisplayName(selectedTerm)}
                />
            ),
        },
        {
            key: 'decision-emails',
            title: 'Decision emails',
            content: <DecisionEmailTemplates programType="ta" />,
        },
        {
            key: 'program-page',
            title: 'Program page link',
            content: <ProgramPageLinkCard programType="ta" programLabel="TA" />,
        },
    ];

    if (!selectedTerm && !loading) {
        return (
            <Container className="py-5">
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <Alert variant="warning">
                        No terms configured. Ask an administrator to create a
                        term in the Admin panel.
                    </Alert>
                )}
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <ProgramTermHeader
                programType="ta"
                programLabel="TA"
                programName="Teaching Assistants"
                programColor="var(--psh-ta-color)"
                terms={terms}
                selectedTerm={selectedTerm}
                onTermChange={handleTermChange}
                stats={displayStats}
                statsLoading={loading}
                statsFailed={loadFailed}
                compact={activeTab !== 'overview'}
            />

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {loadFailed ? (
                <LoadError
                    what="the TA program data"
                    onRetry={() => loadTermData(selectedTerm.code)}
                />
            ) : loading ? (
                <LoadingState rows={4} label="Loading TA program data" />
            ) : (
                <Tab.Container
                    activeKey={activeTab}
                    generateChildId={generateChildId}
                >
                    <DashboardNav
                        ariaLabel="Dashboard sections"
                        groups={navGroups}
                        activeTab={activeTab}
                        onSelect={selectTab}
                    />
                    {activeTab === 'overview' && (
                        <>
                            <MissingDatesAlert
                                programType="ta"
                                programLabel="TA"
                                selectedTerm={selectedTerm}
                                onManageDates={manageDates}
                            />
                            {!loadFailed && (
                                <BudgetStatus
                                    budget={budget}
                                    failed={budgetFailed}
                                    loading={loading}
                                    onRetry={() =>
                                        loadTermData(selectedTerm.code)
                                    }
                                    termName={termDisplayName(selectedTerm)}
                                    unit="TA"
                                    unitPlural="TAs"
                                />
                            )}
                        </>
                    )}
                    <Tab.Content>
                        <Tab.Pane eventKey="overview" mountOnEnter>
                            <CoordinatorOverview
                                active={activeTab === 'overview'}
                                programType="ta"
                                term={selectedTerm?.code}
                                termName={termDisplayName(selectedTerm)}
                                tabs={{ requests: 'requests', positions: null }}
                            />
                        </Tab.Pane>

                        {/* Faculty Requests Tab */}
                        <Tab.Pane eventKey="requests">
                            {requestList && (
                                <ListFilterNote
                                    show={show}
                                    list={requestList}
                                    count={listedRequests.length}
                                    programLabel="TA"
                                    termName={termDisplayName(selectedTerm)}
                                    noun="requests"
                                    onClear={clearShow}
                                />
                            )}
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={requestControls}
                                        noun="requests"
                                        searchTitle="Search TA requests"
                                        searchLabel="Search TA requests by faculty, student or course"
                                        searchPlaceholder="Faculty, student or course"
                                        statusLabel="Filter by request status"
                                        statusOptions={REQUEST_STATUS_OPTIONS}
                                        departmentOptions={
                                            requestDepartmentOptions
                                        }
                                        exportFilename={`ta-requests-${selectedTerm?.code}.csv`}
                                        total={cutOffTotal}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {cutOffTotal !== null && (
                                        <Alert
                                            variant="warning"
                                            className="m-3"
                                        >
                                            Showing the newest {requests.length}{' '}
                                            of {cutOffTotal} requests for this
                                            term. Older ones aren&apos;t listed,
                                            so search and the filters don&apos;t
                                            cover them.
                                        </Alert>
                                    )}
                                    {asCards ? (
                                        <RequestCardList
                                            label="TA requests"
                                            items={requestControls.rows.map(
                                                requestCardOf
                                            )}
                                        />
                                    ) : (
                                        <Table
                                            responsive
                                            hover
                                            className="mb-0"
                                        >
                                            <thead className="bg-light">
                                                <tr>
                                                    {REQUEST_COLUMNS.map(
                                                        (column) => (
                                                            <SortableTh
                                                                key={column.key}
                                                                column={column}
                                                                controls={
                                                                    requestControls
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    <th
                                                        className={
                                                            SECONDARY_COLUMN
                                                        }
                                                    >
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {requestControls.rows.map(
                                                    (req) => (
                                                        <tr key={req._id}>
                                                            <td>
                                                                <strong>
                                                                    {req
                                                                        .requestingFaculty
                                                                        ?.name ||
                                                                        'Unknown'}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {req
                                                                        .requestingFaculty
                                                                        ?.department ||
                                                                        '-'}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                <span className="fw-semibold me-1">
                                                                    {
                                                                        req
                                                                            .course
                                                                            ?.subject
                                                                    }{' '}
                                                                    {
                                                                        req
                                                                            .course
                                                                            ?.courseNumber
                                                                    }
                                                                </span>
                                                                <br />
                                                                <small>
                                                                    {
                                                                        req
                                                                            .course
                                                                            ?.title
                                                                    }
                                                                </small>
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                <strong>
                                                                    {req
                                                                        .namedStudent
                                                                        ?.name ||
                                                                        'Not specified'}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {req
                                                                        .namedStudent
                                                                        ?.studentId ||
                                                                        '-'}
                                                                </small>
                                                                {req.accountMismatch && (
                                                                    <div>
                                                                        <Badge
                                                                            bg="warning"
                                                                            text={badgeTextFor(
                                                                                'warning'
                                                                            )}
                                                                            className="text-wrap text-start"
                                                                        >
                                                                            {
                                                                                ACCOUNT_MISMATCH_BADGE
                                                                            }
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                {req.requestedHours ||
                                                                    '-'}{' '}
                                                                hrs/wk
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                {req.submittedAt
                                                                    ? formatAnswerDay(
                                                                          req.submittedAt
                                                                      )
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                <StatusPill
                                                                    family="taRequest"
                                                                    value={
                                                                        req.status
                                                                    }
                                                                />
                                                            </td>
                                                            <td
                                                                className={
                                                                    SECONDARY_COLUMN
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleOpenReview(
                                                                            req
                                                                        )
                                                                    }
                                                                >
                                                                    Review
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </Table>
                                    )}
                                    <TableEmpty
                                        controls={requestControls}
                                        noun="requests"
                                        emptyText={`No TA requests found for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                            <UnsubmittedDrafts
                                programType="ta"
                                term={selectedTerm?.code}
                                forceOpen={
                                    activeTab === 'requests' &&
                                    show === DRAFTS_SHOW
                                }
                            />
                        </Tab.Pane>

                        {/* Active TAs Tab */}
                        <Tab.Pane eventKey="active">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white">
                                    <TableControlsBar
                                        controls={assignmentControls}
                                        noun="TAs"
                                        searchLabel="Search TAs by name, supervisor or course"
                                        searchPlaceholder="Search by TA, supervisor, or course..."
                                        statusLabel="Filter TAs by course"
                                        allLabel="All courses"
                                        statusOptions={assignmentCourseOptions}
                                        exportFilename={`ta-active-${selectedTerm?.code}.csv`}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                {ASSIGNMENT_COLUMNS.map(
                                                    (column) => (
                                                        <SortableTh
                                                            key={column.key}
                                                            column={column}
                                                            controls={
                                                                assignmentControls
                                                            }
                                                        />
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignmentControls.rows.map(
                                                (ta) => (
                                                    <tr key={ta._id}>
                                                        <td>
                                                            <strong>
                                                                {ta
                                                                    .peerSupporter
                                                                    ?.name ||
                                                                    'Unknown'}
                                                            </strong>
                                                            <small className="text-muted d-none d-md-block">
                                                                {
                                                                    ta
                                                                        .peerSupporter
                                                                        ?.email
                                                                }
                                                            </small>
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {ta.supervisor
                                                                ?.name ||
                                                                ta.instructorName ||
                                                                '-'}
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg="secondary"
                                                                className="me-1"
                                                            >
                                                                {
                                                                    ta.course
                                                                        ?.subject
                                                                }{' '}
                                                                {
                                                                    ta.course
                                                                        ?.courseNumber
                                                                }
                                                            </Badge>
                                                            <br />
                                                            <small>
                                                                {
                                                                    ta.course
                                                                        ?.title
                                                                }
                                                            </small>
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {ta.hoursPerWeek ||
                                                                '-'}
                                                        </td>
                                                        <td
                                                            className={
                                                                SECONDARY_COLUMN
                                                            }
                                                        >
                                                            {ta.startDate
                                                                ? formatCalendarDate(
                                                                      ta.startDate
                                                                  )
                                                                : '-'}
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg={
                                                                    ta.status ===
                                                                    'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                }
                                                                text={badgeTextFor(
                                                                    ta.status ===
                                                                        'active'
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                )}
                                                            >
                                                                {ta.status ===
                                                                'active'
                                                                    ? 'Active'
                                                                    : ta.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                    <TableEmpty
                                        controls={assignmentControls}
                                        noun="TAs"
                                        emptyText={`No active TA assignments for ${termDisplayName(selectedTerm) || 'this term'}.`}
                                    />
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        <Tab.Pane eventKey="settings" mountOnEnter>
                            <CoordinatorSettings
                                focusKey={
                                    activeTab === 'settings' ? show : null
                                }
                                sections={settingsSections}
                                onSelect={(key) => selectTab('settings', key)}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            )}

            {/* Review Modal */}
            <Modal
                aria-labelledby="tacd-modal-title-1"
                show={showReviewModal}
                onHide={closeReview}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="tacd-modal-title-1">
                        {confirming === 'approve' && 'Approve this TA request?'}
                        {confirming === 'reject' && 'Decline this TA request?'}
                        {!confirming && 'Review TA Request'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {decisionError && (
                        <Alert variant="danger">{decisionError}</Alert>
                    )}
                    {selectedRequest && confirming && (
                        <>
                            <dl className="row mb-3">
                                <dt className="col-sm-4">Request</dt>
                                <dd className="col-sm-8">
                                    {courseCode(selectedRequest.course) ||
                                        'Course'}
                                    {', requested by '}
                                    {selectedRequest.requestingFaculty?.name ||
                                        'a faculty member'}
                                </dd>
                                <dt className="col-sm-4">Named student</dt>
                                <dd className="col-sm-8">
                                    {selectedRequest.namedStudent?.name ||
                                        'Not specified'}
                                </dd>
                                {decisionNotes && (
                                    <>
                                        <dt className="col-sm-4">Your notes</dt>
                                        <dd className="col-sm-8 text-break">
                                            {decisionNotes}
                                        </dd>
                                    </>
                                )}
                            </dl>
                            <p className="mb-0">
                                {confirming === 'approve'
                                    ? 'Approving creates a pending TA assignment for the named student, supervised by the requesting faculty member, and tells the faculty member by email and in the site. Your notes stay internal. It cannot be undone from here.'
                                    : 'Declining closes the request and tells the faculty member it was not selected, by email and in the site, with your notes. It cannot be reopened.'}
                            </p>
                        </>
                    )}
                    {selectedRequest && !confirming && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6>Faculty Information</h6>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr>
                                                <td className="text-muted">
                                                    Name:
                                                </td>
                                                <td>
                                                    <strong>
                                                        {selectedRequest
                                                            .requestingFaculty
                                                            ?.name || 'Unknown'}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Email:
                                                </td>
                                                <td>
                                                    {selectedRequest
                                                        .requestingFaculty
                                                        ?.email || '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Department:
                                                </td>
                                                <td>
                                                    {selectedRequest
                                                        .requestingFaculty
                                                        ?.department || '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={6}>
                                    <h6>Course Information</h6>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr>
                                                <td className="text-muted">
                                                    Course:
                                                </td>
                                                <td>
                                                    <strong>
                                                        {
                                                            selectedRequest
                                                                .course?.subject
                                                        }{' '}
                                                        {
                                                            selectedRequest
                                                                .course
                                                                ?.courseNumber
                                                        }
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">
                                                    Title:
                                                </td>
                                                <td>
                                                    {selectedRequest.course
                                                        ?.title || '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>

                            <Alert variant="warning" className="mb-4">
                                <h6 className="mb-2">
                                    Named Student (Required)
                                </h6>
                                <Table size="sm" borderless className="mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted coordinator-named-student-label">
                                                Name:
                                            </td>
                                            <td>
                                                <strong>
                                                    {selectedRequest
                                                        .namedStudent?.name ||
                                                        'Not specified'}
                                                </strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                Email:
                                            </td>
                                            <td>
                                                {selectedRequest.namedStudent
                                                    ?.email || '-'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                Student ID:
                                            </td>
                                            <td>
                                                {selectedRequest.namedStudent
                                                    ?.studentId || '-'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                                {selectedRequest.accountMismatch && (
                                    <p className="mb-0 mt-2">
                                        <strong>
                                            The 900 number and email on this
                                            request belong to different
                                            accounts.
                                        </strong>{' '}
                                        Check both with the faculty member
                                        before approving.
                                    </p>
                                )}
                            </Alert>

                            {selectedRequest.justification && (
                                <div className="mb-3">
                                    <h6>Justification</h6>
                                    <p className="small bg-light p-3 rounded">
                                        {selectedRequest.justification}
                                    </p>
                                </div>
                            )}

                            <div className="mb-3">
                                <h6>Decision Notes</h6>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add optional notes about this decision..."
                                    value={decisionNotes}
                                    onChange={(e) =>
                                        setDecisionNotes(e.target.value)
                                    }
                                    disabled={submittingDecision}
                                />
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {confirming ? (
                        <ActionBar
                            className="w-100"
                            secondary={
                                <Button
                                    variant="outline-primary"
                                    onClick={() => setConfirming(null)}
                                    disabled={submittingDecision}
                                >
                                    Go back
                                </Button>
                            }
                            primary={
                                <Button
                                    ref={focusConfirm}
                                    variant={
                                        confirming === 'reject'
                                            ? 'danger'
                                            : 'primary'
                                    }
                                    onClick={() => handleDecision(confirming)}
                                    disabled={submittingDecision}
                                >
                                    {submittingDecision
                                        ? 'Submitting...'
                                        : confirming === 'reject'
                                          ? 'Decline request'
                                          : 'Approve & Send to HR'}
                                </Button>
                            }
                        />
                    ) : (
                        <ActionBar
                            className="w-100"
                            secondary={[
                                <Button
                                    key="cancel"
                                    variant="outline-primary"
                                    onClick={closeReview}
                                >
                                    Cancel
                                </Button>,
                                <Button
                                    key="decline"
                                    ref={focusDeclineIfAsked}
                                    variant="danger"
                                    onClick={() => {
                                        askedRef.current = 'reject';
                                        setConfirming('reject');
                                    }}
                                >
                                    Decline
                                </Button>,
                            ]}
                            primary={
                                <Button
                                    ref={focusApproveIfAsked}
                                    variant="primary"
                                    onClick={() => {
                                        askedRef.current = 'approve';
                                        setConfirming('approve');
                                    }}
                                >
                                    Approve & Send to HR
                                </Button>
                            }
                        />
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default TACoordinatorDashboard;
